import { MONK_SWATCHES } from '@omnialera/ori-signal';
import { Notice, Pill, Why, percent } from '../components/ui.jsx';

export default function Pledge({ coverage, connected, offline }) {
  // `known` separates "nobody has contributed at this tone" from "the corpus has
  // not told us yet". They looked identical: an unset coverage defaulted to ten
  // zeros and every bar asserted a confident 0.0%, which is a figure the corpus
  // never supplied — the one place in the app printing a number nothing
  // produced. The counter beside it already rendered an honest em dash.
  const known = Boolean(coverage?.byTone?.counts);
  const counts = coverage?.byTone?.counts ?? new Array(10).fill(0);
  const total = counts.reduce((a, b) => a + b, 0) || 1;

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">The Skin Pledge</p>
        <h1>Every shared reading is one more face the next model can see</h1>
        <p>
          This counter is shared. Donate a reading and it moves for everyone looking at it, including the operator
          console.
        </p>
      </div>

      {offline ? (
        <Notice kind="warn" title="Not connected">
          The corpus counter is unavailable right now. Your readings are still recorded on this device and will be
          shared when the connection returns, if you have asked for that.
        </Notice>
      ) : null}

      <div className="grid2">
        <div className="card">
          <div className="row">
            <div>
              <p className="eyebrow">Readings in the corpus</p>
              <span
                className="mono tabular"
                style={{ fontSize: '2.6rem', display: 'block', marginTop: '6px' }}
              >
                {coverage ? coverage.readings.toLocaleString() : '—'}
              </span>
            </div>
            <Pill kind={connected ? 'ok' : 'off'}>{connected ? 'live' : 'offline'}</Pill>
          </div>

          <div className="trio" style={{ marginTop: 'var(--s2)' }}>
            <div className="stat">
              <span className="k">From darker tones</span>
              <span className="v">{coverage ? percent(coverage.underserved.share, 1) : '—'}</span>
            </div>
            <div className="stat">
              <span className="k">Success there</span>
              <span className="v">{coverage ? percent(coverage.underserved.lockRate, 0) : '—'}</span>
            </div>
            <div className="stat">
              <span className="k">People</span>
              <span className="v">{coverage ? coverage.devices.toLocaleString() : '—'}</span>
            </div>
          </div>

          <p style={{ fontSize: '0.95rem', color: 'var(--muted)', margin: 'var(--s2) 0 0', lineHeight: 1.6 }}>
            Across 100 published studies of this technology, the lightest three skin tones made up a median{' '}
            <strong style={{ color: 'var(--ink-soft)' }}>45%</strong> of training data. Everyone else — most of the
            world — made up under <strong style={{ color: 'var(--ink-soft)' }}>25%</strong>. That imbalance is what
            this counter exists to close.
          </p>
        </div>

        <div className="card">
          <p className="eyebrow">Coverage by skin tone</p>
          <div style={{ marginTop: 'var(--s2)' }}>
            {counts.map((count, i) => {
              const pct = known ? (count / total) * 100 : 0;
              return (
                <div key={MONK_SWATCHES[i]} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      flex: 'none',
                      background: MONK_SWATCHES[i],
                      border: '1px solid var(--rule-lit)',
                    }}
                  />
                  <span className="mono" style={{ fontSize: '0.8rem', color: 'var(--faint)', width: 22 }}>
                    {i + 1}
                  </span>
                  <span style={{ flex: 1, height: 9, background: 'var(--surface-3)' }}>
                    <span
                      style={{
                        display: 'block',
                        height: '100%',
                        width: `${pct.toFixed(1)}%`,
                        background: i >= 6 ? 'var(--signal)' : 'var(--pulse)',
                      }}
                    />
                  </span>
                  <span
                    className="mono tabular"
                    style={{ fontSize: '0.82rem', color: 'var(--muted)', width: 52, textAlign: 'right' }}
                  >
                    {known ? `${pct.toFixed(1)}%` : '—'}
                  </span>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: '0.9rem', color: 'var(--faint)', margin: 'var(--s2) 0 0', lineHeight: 1.6 }}>
            Green bars are the tones the field currently fails. Growing them is the entire project.
          </p>
        </div>
      </div>

      <Why>
        <strong>Most data moats require you to obscure what you are doing. This one requires you to publicise it.</strong>{' '}
        The contributor knows exactly what they gave, what it is for, and can watch the gap close. No competitor can buy
        the resulting corpus, because it was not bought — it was volunteered, by people who had a reason.
      </Why>
    </section>
  );
}
